import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { projectConfig } from '@chancenwerk/config.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { createHash } from 'crypto';

/**
 * Time to live for the cache in milliseconds
 */
const GOOGLE_MATRIX_API_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 356;

/**
 * Maximum number of requests per `window` milliseconds
 */
const GOOGLE_MATRIX_API_RATE_LIMIT = 50;

/**
 * Time window in milliseconds for the rate limit
 */
const GOOGLE_MATRIX_API_RATE_LIMIT_WINDOW_MS = 1000;

/**
 * List of timestamps of the last API requests, used for rate limiting.
 */
let apiRequestTimestamps: number[] = [];

/**
 * Response from the Google Distance Matrix API
 */
interface GoogleDistanceMatrixResponse {
  destination_addresses: string[];
  origin_addresses: string[];
  rows: {
    elements: {
      distance: {
        text: string;
        value: number;
      };
      duration: {
        text: string;
        value: number;
      };
      status: string;
    }[];
  }[];
  status: string;
}

/**
 * Uses the Google Distance Matrix API to get the driving distance between two addresses. Returns a string with the distance and duration.
 *
 * Reference: https://developers.google.com/maps/documentation/distance-matrix/overview
 *
 * @param from The starting address
 * @param to The destination address
 * @returns The distance and duration as a string
 */
export const getDistanceBetweenAddress = registerQuery({
  type: () => String,
  operationName: 'getDistanceBetweenAddress',
  resolve: (from: string, to: string) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      // * Only accept requests from logged in users
      if (!user) {
        throw new Error('User not found');
      }

      // * Create hash from the input to use as cache key
      const cacheKey = createHash('sha256')
        .update(from + to)
        .digest('hex');

      const col = await getRawCollection<GoogleDistanceMatrixResponse & { _id: string; created: Maybe<Date> }>(
        'google-distance-matrix-cache',
      );

      // * Check if the data is already cached
      // * If the data is older than GOOGLE_MATRIX_API_CACHE_TTL_MS hours, fetch it again
      const cached = await col.findOne({ _id: cacheKey });
      if (cached && cached.created && Date.now() - cached.created.getTime() < GOOGLE_MATRIX_API_CACHE_TTL_MS) {
        const data = cached.rows[0]?.elements[0];
        if (data) {
          return `${data.distance.text} (${data.duration.text})`;
        }
      }

      // * Delete all timestamps older than GOOGLE_MATRIX_API_RATE_LIMIT_WINDOW_MS ms
      const now = Date.now();
      apiRequestTimestamps = apiRequestTimestamps.filter(
        timestamp => now - timestamp < GOOGLE_MATRIX_API_RATE_LIMIT_WINDOW_MS,
      );

      // * Only allow GOOGLE_MATRIX_API_RATE_LIMIT requests per GOOGLE_MATRIX_API_RATE_LIMIT_WINDOW_MS ms
      if (apiRequestTimestamps.length >= GOOGLE_MATRIX_API_RATE_LIMIT) {
        throw new Error('Too many requests');
      }

      const res = (await fetch(
        `https://maps.googleapis.com/maps/api/distancematrix/json?destinations=${to}&origins=${from}&units=metric&key=${projectConfig.get(
          'ADDRESS_GOOGLE_API_KEY',
        )}&language=de`,
      ).then(res => res.json())) as Maybe<GoogleDistanceMatrixResponse>;

      // * Add the current timestamp to the list for rate limiting
      apiRequestTimestamps.push(now);

      if (!res || res.status !== 'OK') {
        throw new Error('API error');
      }

      const data = res.rows[0]?.elements[0];
      if (!data || data.status !== 'OK') {
        throw new Error('API error');
      }

      // * Cache the result
      await col.updateOne(
        { _id: cacheKey },
        { $set: { _id: cacheKey, created: new Date(), ...res } },
        { upsert: true },
      );

      return `${data.distance.text} (${data.duration.text})`;
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'from' },
    { type: () => String, name: 'to' },
  ],
});

/**
 * Fetch the distance between multiple *cached* addresses in one request. Returns an array of strings with the distance and duration.
 *
 * @param from The starting addresses
 * @param to The destination addresses
 * @returns An array of strings with the distance and duration
 */
export const getDistanceBetweenMultipleAddressCached = registerQuery({
  type: () => [String],
  operationName: 'getDistanceBetweenMultipleAddressCached',
  resolve: (from: string[], to: string[]) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      // * Only accept requests from logged in users
      if (!user) {
        throw new Error('User not found');
      }

      const keys = from.map((f, i) =>
        createHash('sha256')
          .update(f + to[i])
          .digest('hex'),
      );

      const col = await getRawCollection<GoogleDistanceMatrixResponse & { _id: string; created: Maybe<Date> }>(
        'google-distance-matrix-cache',
      );

      // * Get all cached results
      const cached = (await col
        .aggregate([
          {
            $match: {
              _id: {
                $in: keys,
              },
              created: {
                $gt: new Date(Date.now() - GOOGLE_MATRIX_API_CACHE_TTL_MS),
              },
              status: 'OK',
            },
          },

          {
            $project: {
              _id: 1,
              distance: '$rows.elements.distance.text',
              duration: '$rows.elements.duration.text',
            },
          },

          { $unwind: '$distance' },
          { $unwind: '$duration' },

          { $unwind: '$distance' },
          { $unwind: '$duration' },

          {
            $project: {
              _id: 1,
              result: {
                $concat: ['$distance', ' (', '$duration', ')'],
              },
            },
          },
        ])
        .toArray()) as { _id: string; result: string }[];

      // * Map the cached results to the input keys for faster lookup
      const mappedById: Record<string, string> = {};
      cached.forEach(c => {
        mappedById[c._id] = c.result;
      });

      // * Return the results in the same order as the input
      const result = keys.map(key => {
        return mappedById[key] ?? '';
      });

      return result;
    };
  },
  options: {},
  params: [
    { type: () => [String], name: 'from' },
    { type: () => [String], name: 'to' },
  ],
});

export namespace DistanceAPI {
  export const getDistanceBetween = getDistanceBetweenAddress;
  export const getDistanceBetweenMultipleCached = getDistanceBetweenMultipleAddressCached;
  export const isDistanceCached = registerQuery({
    type: () => Boolean,
    operationName: 'isDistanceCached',
    resolve: (from: string, to: string) => {
      return async () => {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
          roles: { name: 1, permissions: 1, contexts: 1 },
        });

        // * Only accept requests from logged in users
        if (!user) {
          throw new Error('User not found');
        }

        // * Create hash from the input to use as cache key
        const cacheKey = createHash('sha256')
          .update(from + to)
          .digest('hex');

        const col = await getRawCollection<GoogleDistanceMatrixResponse & { _id: string; created: Maybe<Date> }>(
          'google-distance-matrix-cache',
        );

        // * Check if the data is already cached
        const cached = await col.findOne({ _id: cacheKey });
        if (cached && cached.created && Date.now() - cached.created.getTime() < GOOGLE_MATRIX_API_CACHE_TTL_MS) {
          const data = cached.rows[0]?.elements[0];
          if (data) {
            return true;
          }
        }
        return false;
      };
    },
    options: {},
    params: [
      { type: () => String, name: 'from' },
      { type: () => String, name: 'to' },
    ],
  });
}
